/**
 * The two AI assessors, side by side, on the same piece of work.
 *
 * Exists to answer one question — is the second provider as good as the first?
 * — so it shows both raw outputs rather than a verdict. Marking an answer and
 * drafting questions are both here, because that question applies to each.
 *
 * Admin only. The payloads quote learners' answers verbatim.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../../api/client";

type Payload = Record<string, unknown> | null;

interface ProviderResult {
  provider: string;
  ok: boolean;
  latency_ms: number;
  error: string | null;
  payload: Payload;
}

interface Entry {
  id: number;
  kind: "assessment" | "generation";
  at: string;
  context: Record<string, unknown>;
  providers: ProviderResult[];
  delta?: number;
  disagreement?: boolean;
}

interface Snapshot {
  entries: Entry[];
  total: number;
  disagreement_delta: number;
  enabled: boolean;
  reason?: string;
  primary: string;
  providers_configured: string[];
  provider_models?: Record<string, string | null>;
  aggregates: {
    assessments: number;
    generations: number;
    compared_scores: number;
    mean_abs_delta: number | null;
    agreement_rate: number | null;
    providers: Record<
      string,
      { calls: number; failures: number; mean_latency_ms: number | null }
    >;
  };
}

const KINDS = [
  { key: "", label: "Everything" },
  { key: "assessment", label: "Marking" },
  { key: "generation", label: "Question drafting" },
] as const;

const PROVIDER_NAMES: Record<string, string> = {
  anthropic: "Anthropic",
  azure: "Azure",
  google: "Google",
};

/** "claude-sonnet-5 (Anthropic)": the model that did the work, then who runs it. */
function assessorLabel(provider: string, models?: Record<string, string | null>): string {
  const company = PROVIDER_NAMES[provider] ?? provider;
  const model = models?.[provider];
  return model ? `${model} (${company})` : company;
}

function str(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "number") return String(value);
  return String(value) || "—";
}

/** One provider's marking, field by field, so the two columns line up. */
function AssessmentPayload({ payload }: { payload: Payload }) {
  if (!payload) return null;
  const rows: [string, unknown][] = [
    ["Score", payload.score],
    ["Concept coverage", payload.concept_coverage],
    ["Reasoning depth", payload.reasoning_depth],
    ["Covered", payload.covered_concepts],
    ["Missing", payload.missing_concepts],
    ["Misconceptions", payload.misconceptions],
    ["Calculation errors", payload.calculation_errors],
    ["Feedback", payload.feedback],
    ["Evidence", payload.evidence],
  ];
  return (
    <dl className="cmp-fields">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{str(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/** One provider's drafted questions. */
function GenerationPayload({ payload }: { payload: Payload }) {
  const questions = (payload?.questions ?? []) as Record<string, unknown>[];
  if (!questions.length) return <p className="text-muted">No questions returned.</p>;
  return (
    <ol className="cmp-questions">
      {questions.map((q, i) => (
        <li key={i}>
          <p className="cmp-q-text">{str(q.text)}</p>
          <p className="cmp-q-meta">
            <span className="badge">{str(q.type)}</span>{" "}
            targets: {str(q.expected_concepts)}
          </p>
        </li>
      ))}
    </ol>
  );
}

export function GraderComparisonPage() {
  const [kind, setKind] = useState<string>("");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["grader-comparisons", kind],
    queryFn: () =>
      apiGet<Snapshot>(
        `/api/admin/grader-comparisons${kind ? `?kind=${kind}` : ""}`,
      ),
    refetchInterval: 15_000,
  });

  if (isLoading) return <p className="text-muted">Loading comparisons…</p>;
  if (isError || !data)
    return (
      <div className="card">
        <p className="text-muted" style={{ margin: 0 }}>
          Couldn't load the comparisons.
        </p>
        <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );

  const agg = data.aggregates;

  return (
    <div className="cmp-page">
      <h1>AI assessor comparison</h1>
      <p className="text-muted">
        The same answer, and the same drafting brief, put to every configured
        assessor. Only{" "}
        <strong>{assessorLabel(data.primary, data.provider_models)}</strong>{" "}
        reaches learners — the others run alongside so they can be judged.
      </p>

      {!data.enabled && (
        <div className="notice" role="status">
          <strong>Comparison is off.</strong> {data.reason} Anything below is
          from an earlier run in this process.
        </div>
      )}

      <div className="cmp-stats">
        <div className="stat-card">
          <span className="stat-value">{agg.assessments}</span>
          <span className="stat-label">answers marked</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{agg.generations}</span>
          <span className="stat-label">drafting runs</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {agg.mean_abs_delta === null ? "—" : agg.mean_abs_delta.toFixed(3)}
          </span>
          <span className="stat-label">mean score gap</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {agg.agreement_rate === null
              ? "—"
              : `${Math.round(agg.agreement_rate * 100)}%`}
          </span>
          <span className="stat-label">
            agree within {data.disagreement_delta}
          </span>
        </div>
      </div>

      <div className="card">
        <h3>Per assessor</h3>
        <table>
          <thead>
            <tr>
              <th>Assessor</th>
              <th>Calls</th>
              <th>Failures</th>
              <th>Mean latency</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(agg.providers).map(([name, s]) => (
              <tr key={name}>
                <td>
                  {assessorLabel(name, data.provider_models)}
                  {name === data.primary && (
                    <span className="badge badge-enrolled"> primary</span>
                  )}
                </td>
                <td>{s.calls}</td>
                <td>{s.failures}</td>
                <td>
                  {s.mean_latency_ms === null
                    ? "—"
                    : `${(s.mean_latency_ms / 1000).toFixed(1)}s`}
                </td>
              </tr>
            ))}
            {!Object.keys(agg.providers).length && (
              <tr>
                <td colSpan={4} className="text-muted">
                  Nothing recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="cmp-filters">
        {KINDS.map((k) => (
          <button
            key={k.key}
            type="button"
            className={`btn btn-sm ${kind === k.key ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setKind(k.key)}
          >
            {k.label}
          </button>
        ))}
      </div>

      {!data.entries.length ? (
        <div className="card">
          <p className="text-muted" style={{ margin: 0 }}>
            Nothing recorded yet. With comparison on, mark one lesson or draft a
            set of questions and every assessor's output will appear here.
          </p>
        </div>
      ) : (
        data.entries.map((entry) => (
          <div className="card cmp-entry" key={entry.id}>
            <header className="cmp-head">
              <span className="badge">
                {entry.kind === "assessment" ? "Marking" : "Question drafting"}
              </span>
              <span className="text-muted">{entry.at}</span>
              {entry.delta !== undefined && (
                <span
                  className={`badge ${entry.disagreement ? "badge-locked" : "badge-enrolled"}`}
                >
                  gap {entry.delta.toFixed(2)}
                  {entry.disagreement ? " — disagree" : ""}
                </span>
              )}
            </header>

            <dl className="cmp-context">
              {Object.entries(entry.context).map(([k, v]) => (
                <div key={k}>
                  <dt>{k.replace(/_/g, " ")}</dt>
                  <dd>{str(v)}</dd>
                </div>
              ))}
            </dl>

            <div className="cmp-columns">
              {entry.providers.map((p) => (
                <section className="cmp-col" key={p.provider}>
                  <h4>
                    {assessorLabel(p.provider, data.provider_models)}
                    {p.provider === data.primary && (
                      <span className="badge badge-enrolled"> primary</span>
                    )}
                    <span className="cmp-latency">
                      {(p.latency_ms / 1000).toFixed(1)}s
                    </span>
                  </h4>
                  {p.error ? (
                    <p className="error-message">{p.error}</p>
                  ) : entry.kind === "assessment" ? (
                    <AssessmentPayload payload={p.payload} />
                  ) : (
                    <GenerationPayload payload={p.payload} />
                  )}
                </section>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
